import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Hyperlink from '@ajat/design-system/Hyperlink';
import useStickySidebar from './useStickySidebar';

/**
 * HelpCenter Sidebar — used by FAQ / Claims / Contact pages.
 *
 * @param {Object[]} topics       - [{ id, label, onClick }]
 * @param {string}   [activeId]   - Highlight the topic with this id
 * @param {string}   [dividerBeforeId] - Render a divider line before this id (desktop)
 * @param {string}   [title]      - Heading text (default "Popular topics")
 *
 * Layout: vertical card on desktop; full-bleed horizontal scroll strip with
 * "/" separators on mobile (Figma 8634:54713 / 8634:56517).
 */
const Sidebar = ({
  topics,
  activeId = null,
  dividerBeforeId = null,
  title = 'Popular topics',
}) => {
  const { sidebarRef, canStick } = useStickySidebar();

  return (
    <aside
      ref={sidebarRef}
      className={`helpcenter__sidebar ${canStick ? 'helpcenter__sidebar--sticky' : ''}`}
    >
      <div className="helpcenter__sidebar-card">
        <h3 className="helpcenter__sidebar-title typo-h4">{title}</h3>
        <ul className="helpcenter__topic-list">
          {topics.map((topic, i) => {
            const isLast = i === topics.length - 1;
            const isActive = topic.id === activeId;
            const showDivider = topic.id === dividerBeforeId;
            const linkClasses = `helpcenter__topic-link${isActive ? ' helpcenter__topic-link--active' : ''}`;
            return (
              <Fragment key={topic.id}>
                {showDivider ? (
                  <li className="helpcenter__topic-divider" aria-hidden="true" />
                ) : null}
                <li className="helpcenter__topic-item">
                  <Hyperlink
                    variant="regular"
                    href={topic.href || '#'}
                    onClick={(e) => {
                      e.preventDefault();
                      topic.onClick?.();
                    }}
                    className={linkClasses}
                  >
                    {topic.label}
                  </Hyperlink>
                </li>
                {!isLast ? (
                  <li className="helpcenter__topic-sep" aria-hidden="true">/</li>
                ) : null}
              </Fragment>
            );
          })}
        </ul>
      </div>
    </aside>
  );
};

Sidebar.propTypes = {
  topics: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      href: PropTypes.string,
    }),
  ).isRequired,
  activeId: PropTypes.string,
  dividerBeforeId: PropTypes.string,
  title: PropTypes.string,
};

export default Sidebar;
